/**
 * Branding Service — White-label Theme Resolution
 *
 * On app load, resolves the current hostname's branding via the API
 * and applies CSS custom properties for theming. Falls back to Airbrx
 * defaults if no custom branding is configured.
 *
 * CSS custom properties applied to :root:
 *   --brand-primary, --brand-secondary, --brand-accent,
 *   --brand-text, --brand-bg, --brand-name, --brand-logo-url
 */

import { createSignal, createResource } from "solid-js";

// ============================================================================
// Types
// ============================================================================

export interface BrandingConfig {
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  textColor: string;
  bgColor: string;
  logoUrl: string | null;
}

export interface BrandingResolveResult {
  branded: boolean;
  accountId?: string;
  branding: BrandingConfig;
}

// ============================================================================
// Defaults
// ============================================================================

export const DEFAULT_BRANDING: BrandingConfig = {
  brandName: "AirBrx",
  primaryColor: "#1a1a2e",
  secondaryColor: "#16213e",
  accentColor: "#0f3460",
  textColor: "#e2e8f0",
  bgColor: "#0f0f23",
  logoUrl: null,
};

// ============================================================================
// CSS Custom Properties
// ============================================================================

/**
 * Apply branding config as CSS custom properties on :root.
 */
export function applyBrandingCss(branding: BrandingConfig): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.style.setProperty("--brand-primary", branding.primaryColor);
  root.style.setProperty("--brand-secondary", branding.secondaryColor);
  root.style.setProperty("--brand-accent", branding.accentColor);
  root.style.setProperty("--brand-text", branding.textColor);
  root.style.setProperty("--brand-bg", branding.bgColor);
  root.style.setProperty("--brand-name", `"${branding.brandName}"`);

  if (branding.logoUrl) {
    root.style.setProperty("--brand-logo-url", `url(${branding.logoUrl})`);
  } else {
    root.style.removeProperty("--brand-logo-url");
  }
}

// ============================================================================
// API Fetcher
// ============================================================================

async function fetchBranding(): Promise<BrandingResolveResult> {
  try {
    const response = await fetch("/api/branding/resolve", {
      credentials: "include",
    });

    if (!response.ok) {
      return { branded: false, branding: DEFAULT_BRANDING };
    }

    return (await response.json()) as BrandingResolveResult;
  } catch {
    return { branded: false, branding: DEFAULT_BRANDING };
  }
}

// ============================================================================
// Reactive Store
// ============================================================================

const [brandingOverride, setBrandingOverride] = createSignal<BrandingConfig | null>(null);

/**
 * Create the branding resource. Call once at app init.
 * Returns a SolidJS resource that resolves the current hostname's branding.
 */
export function createBrandingResource() {
  const [branding, { refetch }] = createResource(fetchBranding);

  return {
    /** The resolved branding result (loading/error states handled by SolidJS) */
    branding,
    /** Force re-fetch branding (e.g., after saving new branding config) */
    refetch,
    /** Override branding locally (for live preview in settings) */
    setPreview: setBrandingOverride,
    /** Clear the local preview override */
    clearPreview: () => setBrandingOverride(null),
  };
}

/**
 * Get the effective branding config, preferring local override.
 */
export function getEffectiveBranding(
  resolved: BrandingResolveResult | undefined,
): BrandingConfig {
  const override = brandingOverride();
  if (override) return override;
  return resolved?.branding ?? DEFAULT_BRANDING;
}
